import { type JSX, useId, useState } from "react";
import {
  CircleStackIcon,
  DocumentIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/outline";
import { FieldType } from "@via/components/advanced-filter/helpers/types";
import {
  FileQueryFilter,
  FilterLogic,
  MetadataFilter,
  PipelineFilter,
} from "@via/pages/sample-trackers/samples/types/filters.ts";
import FilterTags from "@via/pages/sample-trackers/samples/components/filter-tags.tsx";
import AddMetadataFilterModal from "@via/pages/sample-trackers/samples/components/add-metadata-filter-modal.tsx";
import AddPipelineFilterModal from "@via/pages/sample-trackers/samples/components/add-pipeline-filter-modal.tsx";
import AddFileQueryFilterModal from "@via/pages/sample-trackers/samples/components/add-file-query-filter-modal.tsx";
import { Tooltip } from "react-tooltip";

interface FilterSectionProps {
  metadataFilters: MetadataFilter[];
  metadataLogic: FilterLogic;
  onMetadataFiltersChange: (
    filters: MetadataFilter[],
    logic: FilterLogic
  ) => void;
  pipelineFilters: PipelineFilter[];
  pipelineLogic: FilterLogic;
  onPipelineFiltersChange: (
    filters: PipelineFilter[],
    logic: FilterLogic
  ) => void;
  fileQueryFilters: FileQueryFilter[];
  fileQueryLogic: FilterLogic;
  onFileQueryFiltersChange: (
    filters: FileQueryFilter[],
    logic: FilterLogic
  ) => void;
  onClearFilters?: () => void;
  fields: FieldType[];
  projectId?: string;
  disableReports?: boolean;
  reportsDisabledReason?: string;
  extraFilterButtons?: JSX.Element | null;
}

export default function FilterSection({
  metadataFilters,
  metadataLogic,
  onMetadataFiltersChange,
  pipelineFilters,
  pipelineLogic,
  onPipelineFiltersChange,
  fileQueryFilters,
  fileQueryLogic,
  onFileQueryFiltersChange,
  onClearFilters,
  fields,
  projectId,
  disableReports = false,
  reportsDisabledReason,
  extraFilterButtons = null,
}: FilterSectionProps): JSX.Element {
  const reportsTooltipId = useId();
  const [isMetadataModalOpen, setIsMetadataModalOpen] = useState(false);
  const [isPipelineModalOpen, setIsPipelineModalOpen] = useState(false);
  const [isFileQueryModalOpen, setIsFileQueryModalOpen] = useState(false);
  const [editingMetadataIndex, setEditingMetadataIndex] = useState<
    number | null
  >(null);
  const [editingPipelineIndex, setEditingPipelineIndex] = useState<
    number | null
  >(null);
  const [editingFileQueryIndex, setEditingFileQueryIndex] = useState<
    number | null
  >(null);

  const handleAddMetadataFilter = (filter: MetadataFilter): void => {
    if (editingMetadataIndex !== null) {
      // Update existing filter
      const newFilters = [...metadataFilters];
      newFilters[editingMetadataIndex] = filter;
      onMetadataFiltersChange(newFilters, metadataLogic);
      setEditingMetadataIndex(null);
    } else {
      // Add new filter
      onMetadataFiltersChange([...metadataFilters, filter], metadataLogic);
    }
  };

  const handleEditMetadataFilter = (index: number): void => {
    setEditingMetadataIndex(index);
    setIsMetadataModalOpen(true);
  };

  const handleRemoveMetadataFilter = (index: number): void => {
    const newFilters = metadataFilters.filter((_, i) => i !== index);
    onMetadataFiltersChange(newFilters, metadataLogic);
  };

  const handleMetadataLogicChange = (newLogic: FilterLogic): void => {
    onMetadataFiltersChange(metadataFilters, newLogic);
  };

  const handleAddPipelineFilter = (filter: PipelineFilter): void => {
    if (editingPipelineIndex !== null) {
      // Update existing filter
      const newFilters = [...pipelineFilters];
      newFilters[editingPipelineIndex] = filter;
      onPipelineFiltersChange(newFilters, pipelineLogic);
      setEditingPipelineIndex(null);
    } else {
      // Add new filter
      onPipelineFiltersChange([...pipelineFilters, filter], pipelineLogic);
    }
  };

  const handleEditPipelineFilter = (index: number): void => {
    setEditingPipelineIndex(index);
    setIsPipelineModalOpen(true);
  };

  const handleRemovePipelineFilter = (index: number): void => {
    const newFilters = pipelineFilters.filter((_, i) => i !== index);
    onPipelineFiltersChange(newFilters, pipelineLogic);
  };

  const handlePipelineLogicChange = (newLogic: FilterLogic): void => {
    onPipelineFiltersChange(pipelineFilters, newLogic);
  };

  const handleAddFileQueryFilter = (filter: FileQueryFilter): void => {
    if (editingFileQueryIndex !== null) {
      // Update existing filter
      const newFilters = [...fileQueryFilters];
      newFilters[editingFileQueryIndex] = filter;
      onFileQueryFiltersChange(newFilters, fileQueryLogic);
      setEditingFileQueryIndex(null);
    } else {
      // Add new filter
      onFileQueryFiltersChange([...fileQueryFilters, filter], fileQueryLogic);
    }
  };

  const handleEditFileQueryFilter = (index: number): void => {
    setEditingFileQueryIndex(index);
    setIsFileQueryModalOpen(true);
  };

  const handleRemoveFileQueryFilter = (index: number): void => {
    const newFilters = fileQueryFilters.filter((_, i) => i !== index);
    onFileQueryFiltersChange(newFilters, fileQueryLogic);
  };

  const handleFileQueryLogicChange = (newLogic: FilterLogic): void => {
    onFileQueryFiltersChange(fileQueryFilters, newLogic);
  };

  const hasAnyFilters =
    metadataFilters.length > 0 ||
    pipelineFilters.length > 0 ||
    fileQueryFilters.length > 0;

  const reportsButtonDisabled = disableReports || !projectId;
  const reportsButtonTitle = reportsButtonDisabled
    ? reportsDisabledReason || "Reports filter is not available."
    : "Add report filters";

  return (
    <div className="my-4 border border-gray-300 rounded-lg bg-white shadow-sm">
      <div className="p-4 space-y-4">
        {hasAnyFilters && (
          <div className="border-b border-gray-200 pb-4">
            <FilterTags
              metadataFilters={metadataFilters}
              metadataLogic={metadataLogic}
              onRemoveMetadata={handleRemoveMetadataFilter}
              onMetadataLogicChange={handleMetadataLogicChange}
              onEditMetadata={handleEditMetadataFilter}
              pipelineFilters={pipelineFilters}
              pipelineLogic={pipelineLogic}
              onRemovePipeline={handleRemovePipelineFilter}
              onPipelineLogicChange={handlePipelineLogicChange}
              onEditPipeline={handleEditPipelineFilter}
              fileQueryFilters={fileQueryFilters}
              fileQueryLogic={fileQueryLogic}
              onRemoveFileQuery={handleRemoveFileQueryFilter}
              onFileQueryLogicChange={handleFileQueryLogicChange}
              onEditFileQuery={handleEditFileQueryFilter}
            />
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              Add filters:
            </span>
            <div className="flex gap-2">
              {extraFilterButtons}
              <button
                type="button"
                onClick={() => {
                  setEditingMetadataIndex(null);
                  setIsMetadataModalOpen(true);
                }}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors">
                <CircleStackIcon className="h-5 w-5" />
                Metadata
              </button>

              <button
                type="button"
                onClick={() => {
                  setEditingPipelineIndex(null);
                  setIsPipelineModalOpen(true);
                }}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors">
                <RocketLaunchIcon className="h-5 w-5" />
                Run
              </button>

              <button
                type="button"
                disabled={reportsButtonDisabled}
                onClick={() => {
                  setEditingFileQueryIndex(null);
                  setIsFileQueryModalOpen(true);
                }}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-tooltip-id={
                  reportsButtonDisabled ? reportsTooltipId : undefined
                }>
                <DocumentIcon className="h-5 w-5" />
                Reports
              </button>
              {reportsButtonDisabled && (
                <Tooltip
                  id={reportsTooltipId}
                  place="bottom"
                  content={reportsButtonTitle}
                  className="z-50"
                />
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {hasAnyFilters && onClearFilters && (
              <button
                type="button"
                onClick={onClearFilters}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      <AddMetadataFilterModal
        isOpen={isMetadataModalOpen}
        onClose={() => {
          setIsMetadataModalOpen(false);
          setEditingMetadataIndex(null);
        }}
        onAddFilter={handleAddMetadataFilter}
        fields={fields}
        editingFilter={
          editingMetadataIndex !== null
            ? metadataFilters[editingMetadataIndex]
            : undefined
        }
      />
      <AddPipelineFilterModal
        isOpen={isPipelineModalOpen}
        onClose={() => {
          setIsPipelineModalOpen(false);
          setEditingPipelineIndex(null);
        }}
        onAddFilter={handleAddPipelineFilter}
        editingFilter={
          editingPipelineIndex !== null
            ? pipelineFilters[editingPipelineIndex]
            : undefined
        }
      />
      <AddFileQueryFilterModal
        isOpen={isFileQueryModalOpen}
        onClose={() => {
          setIsFileQueryModalOpen(false);
          setEditingFileQueryIndex(null);
        }}
        onAddFilter={handleAddFileQueryFilter}
        projectId={projectId || ""}
        editingFilter={
          editingFileQueryIndex !== null
            ? fileQueryFilters[editingFileQueryIndex]
            : undefined
        }
      />
    </div>
  );
}
